import React, { useMemo } from 'react';

import { AlineAlert } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembersBySeatType } from 'src/hooks';

interface NotEnoughSeatsWarningProps {
  selectedRole: Apollo.OrgRole;
  className?: string;
}

export function NotEnoughSeatsWarning({
  className,
  selectedRole,
}: NotEnoughSeatsWarningProps): JSX.Element | null {
  const {
    basicSeatOrgMembers,
    currentUser,
    fullSeatOrgMembers,
    numBasicSeatsPurchased,
    numFullSeatsPurchased,
  } = useGetOrgMembersBySeatType({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const warningMessage = useMemo(() => {
    // If a user is on the Free plan, no warning to show.
    if (!currentUser?.org.alineSubscription) return null;

    switch (selectedRole) {
      case Apollo.OrgRole.Basic: {
        const isOverLimit = basicSeatOrgMembers.length >= numBasicSeatsPurchased;

        return isOverLimit
          ? `You currently have used ${basicSeatOrgMembers.length} of ${numBasicSeatsPurchased} Basic seat(s) in your plan. We will increase your subscription
      quantity for you when you add a new Basic user to your org.`
          : null;
      }
      case Apollo.OrgRole.Admin:
      case Apollo.OrgRole.Full: {
        const isOverLimit = fullSeatOrgMembers.length >= numFullSeatsPurchased;

        return isOverLimit
          ? `You currently have used ${fullSeatOrgMembers.length} of ${numFullSeatsPurchased} Full seat(s) in your plan. We will increase your subscription
      quantity for you when you add a new Full user to your org.`
          : null;
      }
      default:
        return null;
    }
  }, [
    currentUser?.org.alineSubscription,
    basicSeatOrgMembers.length,
    numBasicSeatsPurchased,
    fullSeatOrgMembers.length,
    numFullSeatsPurchased,
    selectedRole,
  ]);

  if (!warningMessage) return null;

  return (
    <AlineAlert className={className} severity="info">
      {warningMessage}
    </AlineAlert>
  );
}
